import { fetchDocument } from './lumina-request.js';
const { Core, Utils, Events } = Global;

customElements.define('collection-provider', class extends Core {
    elements = { $: [['collection-form'], 'loader'] };
    propTypes = { 'collection-url': String, 'sort-by': String };

    render() {
        this.requestId = 0;
        this.cache = new Map();
        this.urlParams = new URLSearchParams(window.location.search);
        this.urlParams.delete('section_id');
        this.sortBy = this.urlParams.get('sort_by') || this.prop('sort-by');
        this.sortBy && this.urlParams.set('sort_by', this.sortBy);
        this.cache.set(this._cacheKey(this.urlParams), document.cloneNode(true));
        this.sub(Events.COLLECTION_CHANGE, this._handleChange);
        this.sub(Events.COLLECTION_SORT_CHANGE, this._handleSortChange);
        this.$({ click: this._onClick });
        this._onPopState = () => window.location.reload();
        if (this.hasAttribute('data-lumina-history')) window.addEventListener('popstate', this._onPopState);
    }

    _formData(formId) {
        const form = document.getElementById(formId);
        if (!form || !this.contains(form)) throw new Error('Collection form is missing');
        return new FormData(form);
    }

    _handleChange({ src, url, formId, page }) {
        try {
            const params = url
                ? new URL(url, window.location.origin).searchParams
                : new URLSearchParams(this._formData(formId));
            this.sortBy && params.set('sort_by', this.sortBy);
            if (src === 'filter') params.delete('page');
            if (src === 'dynamic-pagination') {
                // The committed URL holds filters from either desktop or drawer.
                const current = new URLSearchParams(this.urlParams);
                current.set('page', String(page));
                return this._load(current, src);
            }
            return this._load(params, src);
        } catch (error) {
            this._failure(error, src);
        }
    }

    _handleSortChange({ value }) {
        const params = new URLSearchParams(this.urlParams);
        params.set('sort_by', value);
        params.delete('page');
        return this._load(params, 'sort');
    }

    _cacheKey(params) {
        const stable = new URLSearchParams(params);
        stable.delete('section_id');
        stable.sort();
        return this.prop('collection-url') + ':' + this.sectionId + ':' + stable.toString();
    }

    async _load(params, src) {
        const id = ++this.requestId;
        this.controller?.abort();
        const controller = new AbortController();
        this.controller = controller;
        const snapshot = new URLSearchParams(params);
        snapshot.delete('section_id');
        this.lastRequest = { params: snapshot, src };
        this._setError(false);
        this.pub('collection:request-start', { src });
        Utils.$loading(this.$('loader'), src !== 'dynamic-pagination');
        this.setAttribute('aria-busy', 'true');
        try {
            const key = this._cacheKey(snapshot);
            const doc = this.cache.get(key) || await fetchDocument(this.prop('collection-url'), {
                params: snapshot, sectionId: this.sectionId, signal: controller.signal
            });
            if (id !== this.requestId || !this.isConnected) return;
            const products = doc.getElementById('collection-products-' + this.sectionId);
            if (!products) throw new Error('Collection response is incomplete');
            if (src === 'dynamic-pagination' && !products.querySelector('[data-element="products-container"]')) {
                throw new Error('Collection page is missing');
            }
            this.pub(Events.COLLECTION_UPDATED, { src, doc });
            for (const block of this.querySelectorAll?.('[data-element="collection-dynamic-block"]') || []) {
                const updated = doc.getElementById(block.id);
                if (updated) block.textContent = updated.textContent;
            }
            this.urlParams = snapshot;
            this.sortBy = snapshot.get('sort_by') || this.prop('sort-by');
            this.cache.set(key, doc);
            while (this.cache.size > 24) this.cache.delete(this.cache.keys().next().value);
            this._updateHistory(src);
            if (src !== 'dynamic-pagination') this._scrollToTop();
        } catch (error) {
            if (id === this.requestId && error.name !== 'AbortError') this._failure(error, src);
        } finally {
            if (id === this.requestId) {
                Utils.$loading(this.$('loader'), false);
                this.setAttribute('aria-busy', 'false');
                this.pub('collection:request-end', { src });
            }
        }
    }

    _failure(error, src) {
        console.error(error);
        this._setError(true);
        this.pub('collection:request-error', { src });
    }

    _setError(visible) {
        const box = this.querySelector('[data-collection-error]');
        if (box) box.hidden = !visible;
    }

    _onClick(event) {
        if (!event.target.closest('[data-collection-retry]') || !this.lastRequest) return;
        this._load(this.lastRequest.params, this.lastRequest.src);
    }

    _updateHistory(src) {
        if (!this.hasAttribute('data-lumina-history')) return;
        const url = new URL(window.location.href);
        url.search = this.urlParams.toString();
        if (url.href === window.location.href) return;
        const method = src === 'dynamic-pagination' ? 'replaceState' : 'pushState';
        window.history[method]({ ...window.history.state, luminaCollection: this.sectionId }, '', url);
    }

    _scrollToTop() {
        const height = parseFloat(document.documentElement.style.getPropertyValue('--header-height')) || 0;
        window.scrollTo({ top: this.getBoundingClientRect().top + window.scrollY - height,
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }

    destroy() {
        ++this.requestId;
        this.controller?.abort();
        window.removeEventListener('popstate', this._onPopState);
    }
});
